
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { hasRole } from "@/utils/auth";
import { supabase } from "@/integrations/supabase/client";
import { 
  Table, 
  TableBody, 
  TableCaption, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { AIAgentManager } from "@/components/admin/AIAgentManager";

interface Profile {
  id: string;
  email: string;
  age_range: string | null;
  education_level: string | null;
  country: string | null;
  created_at: string;
}

const AdminPage = () => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('profiles')
          .select('*');
          
        if (error) {
          throw error;
        }
        
        setProfiles(data || []);
      } catch (err) {
        console.error('Error fetching profiles:', err);
        setError('Failed to load profiles');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfiles();
  }, []);
  
  return (
    <ProtectedRoute requiredRole="admin">
      <Navbar />
      <div className="container mx-auto px-4 py-24">
        <h1 className="text-3xl font-bold mb-6">Admin Dashboard</h1>
        
        <Tabs defaultValue="users" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="users">User Profiles</TabsTrigger>
            <TabsTrigger value="agents">AI Agents</TabsTrigger>
          </TabsList>
          
          <TabsContent value="users">
            <Card className="w-full">
              <CardHeader>
                <CardTitle className="text-2xl">User Profiles</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  </div>
                ) : error ? (
                  <div className="text-destructive text-center py-4">{error}</div>
                ) : (
                  <Table>
                    <TableCaption>List of all user profiles</TableCaption>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Age Range</TableHead>
                        <TableHead>Education Level</TableHead>
                        <TableHead>Country</TableHead>
                        <TableHead>Sign-up Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {profiles.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center">No profiles found</TableCell>
                        </TableRow>
                      ) : (
                        profiles.map((profile) => (
                          <TableRow key={profile.id}>
                            <TableCell>{profile.email}</TableCell>
                            <TableCell>{profile.age_range || 'N/A'}</TableCell>
                            <TableCell>{profile.education_level || 'N/A'}</TableCell>
                            <TableCell>{profile.country || 'N/A'}</TableCell>
                            <TableCell>
                              {profile.created_at 
                                ? new Date(profile.created_at).toLocaleDateString() 
                                : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="agents">
            <AIAgentManager />
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </ProtectedRoute>
  );
};

export default AdminPage;
